import type { ReactNode } from "react";

export interface MockupSpec {
  shape: string;
  imageUrl?: string;
  area: { x: number; y: number; w: number; h: number };
}

type Fit = "contain-top" | "contain-center" | "cover";

const ASPECT: Record<Fit, string> = {
  "contain-top": "xMidYMin meet",
  "contain-center": "xMidYMid meet",
  cover: "xMidYMid slice",
};

/** Shade a hex colour: positive lightens, negative darkens. */
function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex.replace("#", ""), 16);
  const ch = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v + (amount > 0 ? (255 - v) * amount : v * amount))));
  const r = ch((n >> 16) & 255);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function isDark(hex: string) {
  const n = Number.parseInt(hex.replace("#", ""), 16);
  const lum = 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
  return lum < 110;
}

const TEE =
  "M392 92c30 34 186 34 216 0l146 52c26 9 44 26 58 50l96 158c6 10 2 22-8 27l-122 62c-10 5-22 1-27-9l-39-74v478c0 14-11 25-25 25H313c-14 0-25-11-25-25V358l-39 74c-5 10-17 14-27 9l-122-62c-10-5-14-17-8-27l96-158c14-24 32-41 58-50l146-52Z";
const HOODIE =
  "M380 70c40-30 200-30 240 0l150 70c26 12 42 30 54 54l84 180c5 11 0 23-11 28l-110 48c-11 5-23 0-28-11l-23-52v520c0 14-11 25-25 25H289c-14 0-25-11-25-25V387l-23 52c-5 11-17 16-28 11l-110-48c-11-5-16-17-11-28l84-180c12-24 28-42 54-54l150-70Z";

function Garment({ path, color }: { path: string; color: string }) {
  const line = isDark(color) ? shade(color, 0.22) : shade(color, -0.16);
  return (
    <>
      <path d={path} fill={color} stroke={line} strokeWidth={6} strokeLinejoin="round" />
      <path
        d="M392 92c30 44 186 44 216 0"
        fill="none"
        stroke={line}
        strokeWidth={10}
        strokeLinecap="round"
      />
    </>
  );
}

/**
 * Vector product mock-up, tinted to the chosen colour, with the design
 * placed exactly where the print area is.
 */
export function Mockup({
  spec,
  color = "#F7F7F5",
  fit = "contain-top",
  aspect,
  children,
  label,
  className,
}: {
  spec: MockupSpec;
  color?: string | null;
  fit?: Fit;
  /** Artwork width/height so the nested SVG keeps its proportions. */
  aspect: { width: number; height: number };
  children: ReactNode;
  label: string;
  className?: string;
}) {
  const c = color ?? "#F7F7F5";
  const a = spec.area;
  const art = (
    <svg
      aria-hidden="true"
      x={a.x * 1000}
      y={a.y * 1000}
      width={a.w * 1000}
      height={a.h * 1000}
      viewBox={`0 0 ${aspect.width} ${aspect.height}`}
      preserveAspectRatio={ASPECT[fit]}
    >
      {children}
    </svg>
  );
  return (
    <svg className={className} viewBox="0 0 1000 1000" role="img" aria-label={label}>
      <title>{label}</title>
      {spec.imageUrl ? (
        <image
          href={spec.imageUrl}
          width={1000}
          height={1000}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : spec.shape === "hoodie" ? (
        <Garment path={HOODIE} color={c} />
      ) : spec.shape === "poster" ? (
        <rect
          x={170}
          y={60}
          width={660}
          height={880}
          rx={6}
          fill={c}
          stroke={shade(c, -0.15)}
          strokeWidth={6}
        />
      ) : spec.shape === "mug" ? (
        <>
          <path
            d="M800 360c90 0 130 50 130 130s-40 130-130 130"
            fill="none"
            stroke={shade(c, -0.12)}
            strokeWidth={44}
          />
          <rect
            x={150}
            y={250}
            width={660}
            height={560}
            rx={40}
            fill={c}
            stroke={shade(c, -0.15)}
            strokeWidth={6}
          />
        </>
      ) : spec.shape === "tote" ? (
        <>
          <path
            d="M360 300c0-150 280-150 280 0"
            fill="none"
            stroke={shade(c, -0.2)}
            strokeWidth={26}
          />
          <rect
            x={200}
            y={280}
            width={600}
            height={660}
            rx={12}
            fill={c}
            stroke={shade(c, -0.15)}
            strokeWidth={6}
          />
        </>
      ) : (
        <Garment path={TEE} color={c} />
      )}
      {art}
    </svg>
  );
}
